import React from "react";
import { FiMapPin } from "react-icons/fi";
import { SectionHeader } from "../../../components/Shared";

const ASSET_TYPE_META = {
  "Forklift":       { color: "#f59e0b", bg: "#fef3c7", letter: "F" },
  "AGV Robot":      { color: "#8b5cf6", bg: "#ede9fe", letter: "R" },
  "Cargo Cart":     { color: "#3b82f6", bg: "#dbeafe", letter: "C" },
  "Employee Badge": { color: "#10b981", bg: "#d1fae5", letter: "E" },
  "Laptop":         { color: "#6b7280", bg: "#f1f5f9", letter: "L" },
  "Tablet":         { color: "#64748b", bg: "#f8fafc", letter: "T" },
};

const FILTER_TYPES = ["All", "Forklift", "AGV Robot", "Cargo Cart", "Employee Badge", "Laptop"];

const assetStatusColor = (s) =>
  s === "Active" ? null : s === "Charging" ? "#eab308" : "#94a3b8";

const AssetTrackingPanel = ({ d }) => {
  const [selectedAsset, setSelectedAsset] = React.useState(null);
  const [filterType, setFilterType] = React.useState("All");

  const filtered = d.movableAssets.filter(a =>
    filterType === "All" || a.type === filterType || (filterType === "Laptop" && (a.type === "Laptop" || a.type === "Tablet"))
  );

  const handleSelect = (asset) => setSelectedAsset(prev => prev?.id === asset.id ? null : asset);

  const selectedZone = selectedAsset
    ? d.warehouseZones.find(z => z.id === selectedAsset.zone)
    : null;

  return (
    <div>
      <SectionHeader title="Indoor Asset Tracking – Warehouse Floor Plan" />

      {/* Filter bar */}
      <div className="fm-tracking-filter">
        {FILTER_TYPES.map(f => (
          <button key={f} className={`fm-filter-chip ${filterType === f ? "fm-filter-chip-active" : ""}`}
            onClick={() => setFilterType(f)}>
            {f}
            <span className="fm-filter-count">
              {f === "All" ? d.movableAssets.length
                : f === "Laptop" ? d.movableAssets.filter(a => a.type === "Laptop" || a.type === "Tablet").length
                : d.movableAssets.filter(a => a.type === f).length}
            </span>
          </button>
        ))}
      </div>

      {/* Main layout */}
      <div className="fm-tracking-layout">
        {/* Asset List */}
        <div className="fm-asset-list-panel">
          {filtered.map(asset => {
            const meta = ASSET_TYPE_META[asset.type] || ASSET_TYPE_META["Laptop"];
            const pinColor = assetStatusColor(asset.status) || meta.color;
            const isSelected = selectedAsset?.id === asset.id;
            return (
              <div key={asset.id}
                className={`fm-asset-list-item ${isSelected ? "fm-asset-list-selected" : ""}`}
                onClick={() => handleSelect(asset)}>
                <div className="fm-asset-dot" style={{ background: pinColor, color: "white" }}>
                  {meta.letter}
                </div>
                <div className="fm-asset-item-body">
                  <div className="fm-asset-item-name">{asset.name}</div>
                  <div className="fm-asset-item-meta">
                    <span className="fm-asset-zone-chip" style={{
                      background: d.warehouseZones.find(z => z.id === asset.zone)?.fill,
                      color: d.warehouseZones.find(z => z.id === asset.zone)?.stroke,
                      border: `1px solid ${d.warehouseZones.find(z => z.id === asset.zone)?.stroke}`,
                    }}>
                      Zone {asset.zone}
                    </span>
                    <span className={`fm-asset-status-chip ast-${asset.status.toLowerCase()}`}>{asset.status}</span>
                  </div>
                  <div className="fm-asset-item-sub">
                    {asset.battery !== null
                      ? (
                        <>
                          <div className="fm-asset-battery">
                            <div className="fm-asset-battery-fill" style={{
                              width: `${asset.battery}%`,
                              background: asset.battery < 25 ? "#ef4444" : asset.battery < 50 ? "#f59e0b" : "#10b981",
                            }} />
                          </div>
                          <span>{asset.battery}%</span>
                        </>
                      )
                      : <span style={{ color: "#94a3b8" }}>Wired</span>
                    }
                    <span className="fm-asset-lastseen">{asset.lastSeen}</span>
                  </div>
                </div>
                <FiMapPin size={14} color={isSelected ? meta.color : "#cbd5e1"} />
              </div>
            );
          })}
        </div>

        {/* Map Panel */}
        <div className="fm-map-panel">
          <svg
            className="fm-map-svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Background */}
            <rect x="0" y="0" width="100" height="100" fill="#f8fafc" rx="1" />

            {/* Zones */}
            {d.warehouseZones.map(z => (
              <g key={z.id}>
                <rect x={z.x} y={z.y} width={z.w} height={z.h}
                  fill={z.fill}
                  stroke={z.stroke}
                  strokeWidth={selectedAsset?.zone === z.id ? 0.6 : 0.3}
                  rx="0.8"
                  opacity={selectedAsset && selectedAsset.zone !== z.id ? 0.5 : 1}
                />
                <text
                  x={z.x + z.w / 2} y={z.y + 4}
                  textAnchor="middle" dominantBaseline="hanging"
                  fontSize="2.2" fontWeight="700" fill={z.stroke} opacity="0.9"
                >
                  {z.name}
                </text>
                <text
                  x={z.x + z.w / 2} y={z.y + 7}
                  textAnchor="middle" dominantBaseline="hanging"
                  fontSize="1.6" fill={z.stroke} opacity="0.6"
                >
                  Zone {z.id}
                </text>
              </g>
            ))}

            {/* Asset pins – unselected first, selected on top */}
            {[
              ...filtered.filter(a => a.id !== selectedAsset?.id),
              ...filtered.filter(a => a.id === selectedAsset?.id),
            ].map(asset => {
              const meta = ASSET_TYPE_META[asset.type] || ASSET_TYPE_META["Laptop"];
              const pinColor = assetStatusColor(asset.status) || meta.color;
              const isSelected = selectedAsset?.id === asset.id;
              const r = isSelected ? 3.8 : 2.6;
              return (
                <g key={asset.id} style={{ cursor: "pointer" }} onClick={() => handleSelect(asset)}>
                  {/* Pulse ring for active */}
                  {asset.status === "Active" && (
                    <circle cx={asset.x} cy={asset.y} r={r + 1.8}
                      fill="none" stroke={pinColor} strokeWidth="0.4"
                      className="fm-asset-pulse-ring" />
                  )}
                  {/* Pin circle */}
                  <circle cx={asset.x} cy={asset.y} r={r}
                    fill={pinColor}
                    stroke={isSelected ? "white" : "rgba(255,255,255,0.7)"}
                    strokeWidth={isSelected ? 0.8 : 0.4}
                  />
                  {/* Letter */}
                  <text cx={asset.x} cy={asset.y} x={asset.x} y={asset.y}
                    textAnchor="middle" dominantBaseline="central"
                    fontSize={isSelected ? 2.4 : 1.9} fontWeight="800" fill="white"
                    style={{ pointerEvents: "none" }}
                  >
                    {meta.letter}
                  </text>
                  {/* Label for selected */}
                  {isSelected && (
                    <text x={asset.x} y={asset.y + r + 2.2}
                      textAnchor="middle" dominantBaseline="hanging"
                      fontSize="1.8" fontWeight="700" fill={pinColor}
                      style={{ pointerEvents: "none" }}
                    >
                      {asset.id}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div className="fm-map-legend">
            {Object.entries(ASSET_TYPE_META).map(([type, meta]) => (
              <span key={type} className="fm-map-legend-item">
                <span className="fm-map-legend-dot" style={{ background: meta.color }} />
                {type}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Selected Asset Detail */}
      {selectedAsset && (
        <div className="fm-selected-detail">
          <div className="fm-selected-detail-header">
            <div className="fm-selected-dot" style={{ background: ASSET_TYPE_META[selectedAsset.type]?.color || "#6b7280" }}>
              {ASSET_TYPE_META[selectedAsset.type]?.letter}
            </div>
            <div>
              <div className="fm-selected-name">{selectedAsset.name}</div>
              <div className="fm-selected-id">{selectedAsset.id}</div>
            </div>
            <button className="fm-selected-close" onClick={() => setSelectedAsset(null)}>✕</button>
          </div>
          <div className="fm-selected-grid">
            <div className="fm-selected-field">
              <div className="fm-selected-lbl">Type</div>
              <div className="fm-selected-val">
                <span className="fm-asset-type-chip" style={{
                  background: ASSET_TYPE_META[selectedAsset.type]?.bg,
                  color: ASSET_TYPE_META[selectedAsset.type]?.color,
                }}>
                  {selectedAsset.type}
                </span>
              </div>
            </div>
            <div className="fm-selected-field">
              <div className="fm-selected-lbl">Zone Location</div>
              <div className="fm-selected-val">
                <span style={{ fontWeight: 700, color: selectedZone?.stroke }}>
                  Zone {selectedAsset.zone} – {selectedZone?.name}
                </span>
              </div>
            </div>
            <div className="fm-selected-field">
              <div className="fm-selected-lbl">Status</div>
              <div className="fm-selected-val">
                <span className={`fm-asset-status-chip ast-${selectedAsset.status.toLowerCase()}`}>{selectedAsset.status}</span>
              </div>
            </div>
            <div className="fm-selected-field">
              <div className="fm-selected-lbl">Battery</div>
              <div className="fm-selected-val">
                {selectedAsset.battery !== null ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <div className="fm-asset-battery" style={{ width: 80 }}>
                      <div className="fm-asset-battery-fill" style={{
                        width: `${selectedAsset.battery}%`,
                        background: selectedAsset.battery < 25 ? "#ef4444" : selectedAsset.battery < 50 ? "#f59e0b" : "#10b981",
                      }} />
                    </div>
                    <span style={{ fontWeight: 700 }}>{selectedAsset.battery}%</span>
                  </div>
                ) : <span style={{ color: "#94a3b8" }}>Wired / N/A</span>}
              </div>
            </div>
            <div className="fm-selected-field">
              <div className="fm-selected-lbl">Last Seen</div>
              <div className="fm-selected-val" style={{ fontWeight: 600 }}>{selectedAsset.lastSeen}</div>
            </div>
            <div className="fm-selected-field">
              <div className="fm-selected-lbl">Assigned To</div>
              <div className="fm-selected-val" style={{ fontWeight: 600 }}>{selectedAsset.assignedTo}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetTrackingPanel;
